import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import fundingLicensing from "@faire/web-api--source/routes/www/open-with-faire/academy/funding-licensing";
import { lazy } from "react";
import * as React from "react";

const LazyTypesOfFunding = lazy(() =>
  import(
    /* webpackChunkName: "get-funding-licensing-types-of-funding" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/GetFundingAndLicenses/__internal__/TypesOfFunding"
  ).then((m) => ({ default: m.TypesOfFunding }))
);
const LazyRegisteringYourBusiness = lazy(() =>
  import(
    /* webpackChunkName: "get-funding-licensing-registering-your-business" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/GetFundingAndLicenses/__internal__/RegisteringYourBusiness"
  ).then((m) => ({ default: m.RegisteringYourBusiness }))
);

const C1 = "Types of funding";
const TYPES_OF_FUNDING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.TYPES_OF_FUNDING,
  title: C1,
  readTimeMinutes: 5,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyTypesOfFunding,
};

const C2 = "Registering your business";
const REGISTERING_YOUR_BUSINESS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.REGISTERING_YOUR_BUSINESS,
  title: C2,
  readTimeMinutes: 2,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyRegisteringYourBusiness,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  TYPES_OF_FUNDING,
  REGISTERING_YOUR_BUSINESS,
];

export const GET_FUNDING_AND_LICENSES: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.GET_FUNDING_AND_LICENSES,
  title: "Get funding & licenses",
  description: "Understand funding and legal needs for your retail store.",
  cardSummary:
    "Understand your funding options and legal requirements to operate.",
  guideSummary: (
    <>
      Securing adequate funding and obtaining necessary licenses are critical
      steps in launching your retail business. Various financing options are
      available, such as self-funding, bank loans, government grants, and more.
      You'll need to study the advantages and drawbacks of each funding type to
      make informed decisions tailored to your financial needs. There are also
      essential licenses and permits required to legally operate your retail
      business. These ensure you comply with local, state, and federal
      regulations. By understanding the financial and legal frameworks, you can
      establish a business that is not only financially viable but also legally
      sound.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/7e58f7c7dd209f2e572b93bfc071d1be38c8dff1a3015bc581ee1e0c4161db1b.png",
  featuredImageAltText:
    "Woman in floral shirt on her laptop in her retail store",
  backgroundImage:
    "https://cdn.faire.com/fastly/fa26612cc0af3b877fdab537410382bd588a502482233e9c4983512d1afd7c93.png",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: fundingLicensing(),
  titleTag: "How to finance & license your new retail store",
};
